import { useContext, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { RouteComponentProps } from 'react-router-dom'
import { IonContent, IonPage, IonSpinner } from '@ionic/react'

import { Button } from '@/components/atoms/Button'
import { InterestButton } from '@/components/atoms/InterestButton'
import { Loading } from '@/components/atoms/Loading'
import { Title } from '@/components/atoms/Title'

import { Context } from '@lib/store'
import { ActionType } from '@lib/store/reducer'
import {
  SubscribedCategoriesQueryKey,
  useSubscribeCategory,
  useSurveyCategories,
} from '@/lib/hooks/categories'
import {
  UserInterestsStatus,
  useUserInterestsStatus,
} from '@/lib/hooks/useUserInterestsStatus'
import { queryClient } from '@/lib/queryClient'
import { Routes } from '@/router/routes'

const MINIMUM_INTEREST_COUNT = 3
const MAXIMUM_INTEREST_COUNT = 5

export const InterestsSurvey = ({ history }: RouteComponentProps) => {
  const { dispatch } = useContext(Context)

  const { t } = useTranslation('common')
  const [selectedInterests, setSelectedInterests] = useState<string[]>([])

  const { userInterestsStatus, setUserInterestsStatus } =
    useUserInterestsStatus()

  const { data: interestsCategories, isLoading: isLoadingCategories } =
    useSurveyCategories()
  const { mutate: subscribeCategory, isPending: isSubscribing } =
    useSubscribeCategory({
      onSuccess: (data) => {
        queryClient.setQueryData([SubscribedCategoriesQueryKey], data)
      },
      onError: (error) => {
        console.error(`Error subscribe category ${error}`)
      },
      onSettled: () => setUserInterestsStatus(UserInterestsStatus.Done),
    })

  useEffect(() => {
    dispatch({
      type: ActionType.SIGN_IN_REDIRECT,
      payload: undefined,
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (userInterestsStatus === UserInterestsStatus.Done)
      history.replace(Routes.LOGIN)
  }, [history, userInterestsStatus])

  const handleInterestClick = (interestValue: string) => {
    setSelectedInterests((prev) => {
      if (
        prev.length < MAXIMUM_INTEREST_COUNT &&
        !prev.includes(interestValue)
      ) {
        return [...prev, interestValue]
      }

      return prev.filter((value) => value !== interestValue)
    })
  }

  const handleContinue = async () => {
    subscribeCategory(selectedInterests)
  }

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="mx-auto max-w-[40rem] flex flex-col h-full px-10 pt-12 pb-6 text-white">
          <Title className="mt-12 mb-6" level={1}>
            <Trans
              t={t}
              i18nKey={'registration.interests.title'}
              components={{ primary: <span className="text-primary" /> }}
            >
              {t('registration.interests.title')}
            </Trans>
          </Title>
          <span className="text-sm">
            <Trans t={t} i18nKey={'registration.interests.description'}>
              {t('registration.interests.description')}
            </Trans>
          </span>
          <div className="mt-12 flex flex-col flex-1">
            <span className="text-primary text-sm font-bold">
              {t('registration.interests.selectCategories')}
            </span>
            {isLoadingCategories ? (
              <Loading />
            ) : (
              <div className="py-4">
                {interestsCategories.map(({ id, name }) => (
                  <InterestButton
                    key={id}
                    className="mx-2 my-1 ml-0"
                    label={name}
                    value={id}
                    isSelected={selectedInterests.includes(id)}
                    onClick={handleInterestClick}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="h-24">
            <Button
              className="mt-8 mb-12 w-md"
              isDisabled={
                selectedInterests.length < MINIMUM_INTEREST_COUNT ||
                isSubscribing
              }
              onClick={handleContinue}
              size="large"
              type={selectedInterests.length ? 'primary' : 'secondary'}
            >
              {isSubscribing ? (
                <IonSpinner color="light" />
              ) : (
                t('registration.interests.continue')
              )}
            </Button>
          </div>
        </div>
      </IonContent>
    </IonPage>
  )
}
